import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api } from '../api/client';
import { getAllowedSiteSlugsForBrand, hasAnyAdSitePermissionCode } from '../lib/adPermissions';

export type Brand = 'zeelool' | 'vooglam';

// Keep legacy map for backward compatibility during transition
export const BRAND_ID_MAP: Record<Brand, string> = {
  zeelool: '11111111-aaaa-bbbb-cccc-000000000001',
  vooglam: '11111111-aaaa-bbbb-cccc-000000000002',
};

export interface BrandItem {
  id: string;
  name: string;
  logo_url?: string;
  description?: string;
}

export interface SiteItem {
  id: string;
  brand_id: string;
  code: string;
  name: string;
  domain: string;
  currency: string;
  timezone: string;
  is_active: boolean;
  sort: number;
}

export interface ChannelFilters {
  tt: boolean;
  fb: boolean;
  gg: boolean;
}

// 广告平台权限上下文（SSO 登录时后端解析后返回，存 localStorage）
export interface AdContext {
  role: 'viewer' | 'optimizer' | 'lead' | 'admin'
  data_level: number
  brand_access: string[]   // 允许的品牌 UUID 列表
  site_access: Record<string, string[]>  // brand_id → 允许的站点 UUID 列表
  channel_access?: Array<'facebook' | 'google' | 'tiktok'>
  ai_debug_level?: 'none' | 'business' | 'developer'
}

export interface AppState {
  // Brand & Site
  brand: Brand;
  site: string;
  brandId: string;  // UUID from brands table
  siteId: string;   // UUID from sites table
  brands: BrandItem[];
  sites: SiteItem[];
  setBrand: (brand: Brand) => void;
  setSite: (site: string) => void;
  setBrandId: (brandId: string) => void;
  setSiteId: (siteId: string) => void;
  fetchBrands: () => Promise<void>;
  fetchSites: (brandId: string) => Promise<void>;
  selectBrandById: (brandId: string) => void;
  selectSiteById: (siteId: string) => void;

  // Sidebar
  sidebarCollapsed: boolean;
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;

  // Channel Filters
  channelFilters: ChannelFilters;
  toggleChannelFilter: (channel: keyof ChannelFilters) => void;
  setChannelFilter: (channel: keyof ChannelFilters, value: boolean) => void;
  setAllChannelFilters: (filters: ChannelFilters) => void;

  // Date Range
  dateRange: string;
  setDateRange: (range: string) => void;

  // Notifications
  notificationCount: number;
  setNotificationCount: (count: number) => void;
  incrementNotificationCount: () => void;
  clearNotifications: () => void;

  // Permissions
  permissionCodes: string[];
  permissionsLoaded: boolean;
  permissionsLoading: boolean;
  permissionsCheckDone: boolean; // 首次检查完成（含失败），用于放行 Outlet
  isForbidden: boolean;
  setForbidden: (v: boolean) => void;
  fetchPermissions: () => Promise<void>;
  hasPermission: (code: string) => boolean;

  // Sites load flag（用于 Outlet 放行：权限 + 站点都就绪才渲染页面）
  sitesLoaded: boolean;

  // Ad Context（从 GK 解析好的广告权限上下文）
  adContext: AdContext | null
  setAdContext: (ctx: AdContext | null) => void

  // Session
  isLoggedOut: boolean;
  setLoggedOut: (v: boolean) => void;
}

const DEFAULT_SITE_ID = '22222222-aaaa-bbbb-cccc-000000000001';
function normalizeStoredUuid(value: unknown, fallback = ''): string {
  const trimmed = String(value ?? '').trim();
  return trimmed || fallback;
}

function getFirstAccessibleBrand(
  brands: BrandItem[],
  codes: string[],
): BrandItem | undefined {
  return brands.find((brand) => {
    const slugs = getAllowedSiteSlugsForBrand(codes, brand.name?.toLowerCase());
    return slugs.includes('global') || slugs.length > 0;
  });
}

export const useAppStore = create<AppState>()(
  persist(
    (set, get) => ({
      // Brand & Site
      brand: 'zeelool',
      site: 'us-zeelool',
      brandId: BRAND_ID_MAP.zeelool,
      siteId: DEFAULT_SITE_ID,
      brands: [],
      sites: [],

      setBrand: (brand) => set({ brand, site: brand === 'zeelool' ? 'us-zeelool' : 'us-vooglam' }),
      setSite: (site) => set({ site }),
      setBrandId: (brandId) => set({ brandId }),
      setSiteId: (siteId) => set({ siteId }),

      fetchBrands: async () => {
        try {
          const res = await api.get<{ code: number; data: BrandItem[] }>('/brands');
          const brands = res.data || [];
          set({ brands });
          const state = get();
          const allowedBrandIds = state.adContext?.brand_access ?? [];
          if (allowedBrandIds.length > 0 && state.brandId && !allowedBrandIds.includes(state.brandId)) {
            const firstAllowedBrand = brands.find((b) => allowedBrandIds.includes(b.id));
            if (firstAllowedBrand) {
              set({
                brandId: firstAllowedBrand.id,
                brand: (firstAllowedBrand.name.toLowerCase() as Brand) || state.brand,
                siteId: '',
                site: '',
                sites: [],
                sitesLoaded: false,
              });
              get().fetchSites(firstAllowedBrand.id);
              return;
            }
          }
          if (brands.length > 0 && !brands.find(b => b.id === state.brandId)) {
            // brandId not in fetched list → select first brand
            const first = brands[0];
            set({ brandId: first.id, brand: (first.name.toLowerCase() as Brand) || 'zeelool' });
            get().fetchSites(first.id);
          } else if (state.brandId && state.sites.length === 0) {
            // brandId already valid but sites not loaded yet → fetch them
            get().fetchSites(state.brandId);
          }
        } catch (e) {
          console.error('Failed to fetch brands:', e);
        }
      },

      fetchSites: async (brandId: string) => {
        set({ sitesLoaded: false });
        try {
          const res = await api.get<{ code: number; data: SiteItem[] }>(`/brands/${brandId}/sites`);
          const SITE_ORDER = ['us', 'ca', 'uk', 'de', 'fr', 'au'];
          const sites = (res.data || []).sort((a, b) => {
            const ai = SITE_ORDER.indexOf((a.code?.split('-')[0] || '').toLowerCase());
            const bi = SITE_ORDER.indexOf((b.code?.split('-')[0] || '').toLowerCase());
            return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
          });
          set({ sites, sitesLoaded: true });
          const state = get();
          const siteUpdate: { siteId?: string; site?: string } = {};
          // 基础校验：siteId 不在站点列表中 → 选第一个
          if (sites.length > 0 && !sites.find(s => s.id === state.siteId)) {
            siteUpdate.siteId = sites[0].id;
            siteUpdate.site = sites[0].code;
          }
          // 权限校验：若权限已提前加载完（fetchPermissions 先于 fetchSites 完成），
          // 此时再做一次基于权限的 siteId 修正，解决并发竞态导致的 siteId 残留问题
          if (state.permissionsLoaded && state.permissionCodes.length > 0) {
            const brandName = state.brands.find(b => b.id === brandId)?.name?.toLowerCase();
            const hasBrandCodes = hasAnyAdSitePermissionCode(state.permissionCodes);
            if (hasBrandCodes && brandName && sites.length > 0) {
              const slugs = getAllowedSiteSlugsForBrand(state.permissionCodes, brandName);
              if (!slugs.includes('global') && slugs.length > 0) {
                const allowedSlugs = new Set(slugs.map(s => s.toLowerCase()));
                const allowedIds = new Set(
                  sites
                    .filter(s => allowedSlugs.has((s.code?.split('-')[0] || s.code)?.toLowerCase() ?? ''))
                    .map(s => s.id)
                );
                const currentSiteId = siteUpdate.siteId ?? state.siteId;
                if (!allowedIds.has(currentSiteId)) {
                  const first = sites.find(s => allowedIds.has(s.id));
                  if (first) { siteUpdate.siteId = first.id; siteUpdate.site = first.code; }
                }
              }
              if (!slugs.includes('global') && slugs.length === 0) {
                const fallbackBrand = getFirstAccessibleBrand(state.brands, state.permissionCodes);
                if (fallbackBrand && fallbackBrand.id !== brandId) {
                  set({
                    brandId: fallbackBrand.id,
                    brand: (fallbackBrand.name.toLowerCase() as Brand) || 'zeelool',
                    siteId: '',
                    site: '',
                    sites: [],
                    sitesLoaded: false,
                  });
                  void get().fetchSites(fallbackBrand.id);
                  return;
                }
              }
            }
          }
          if (siteUpdate.siteId) set(siteUpdate);
        } catch (e) {
          console.error('Failed to fetch sites:', e);
          set({ sitesLoaded: true });
        }
      },

      selectBrandById: (brandId: string) => {
        const state = get();
        const brandItem = state.brands.find(b => b.id === brandId);
        if (brandItem) {
          set({
            brandId,
            brand: (brandItem.name.toLowerCase() as Brand) || 'zeelool',
            siteId: '',
            site: '',
            sites: [],
            sitesLoaded: false,
          });
          get().fetchSites(brandId);
        }
      },

      selectSiteById: (siteId: string) => {
        const state = get();
        const siteItem = state.sites.find(s => s.id === siteId);
        if (siteItem) {
          set({ siteId, site: siteItem.code });
        }
      },

      // Sidebar
      sidebarCollapsed: false,
      toggleSidebar: () => set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),
      setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),

      // Channel Filters
      channelFilters: {
        tt: true,
        fb: true,
        gg: true,
      },
      toggleChannelFilter: (channel) =>
        set((state) => ({
          channelFilters: {
            ...state.channelFilters,
            [channel]: !state.channelFilters[channel],
          },
        })),
      setChannelFilter: (channel, value) =>
        set((state) => ({
          channelFilters: {
            ...state.channelFilters,
            [channel]: value,
          },
        })),
      setAllChannelFilters: (filters) => set({ channelFilters: filters }),

      // Date Range
      dateRange: '近 7 天',
      setDateRange: (range) => set({ dateRange: range }),

      // Ad Context — SSO 登录后从 localStorage 恢复
      adContext: (() => {
        try {
          const raw = localStorage.getItem('ad_context')
          return raw ? (JSON.parse(raw) as AdContext) : null
        } catch { return null }
      })(),
      setAdContext: (ctx) => {
        if (ctx) {
          localStorage.setItem('ad_context', JSON.stringify(ctx))
        } else {
          localStorage.removeItem('ad_context')
        }
        set({ adContext: ctx })
      },

      // Session
      isLoggedOut: false,
      setLoggedOut: (v) => set({ isLoggedOut: v }),

      // Permissions
      permissionCodes: [],
      permissionsLoaded: false,
      permissionsLoading: false,
      permissionsCheckDone: false,
      sitesLoaded: false,
      isForbidden: false,
      setForbidden: (v) => set({ isForbidden: v }),
      fetchPermissions: async () => {
        set({ permissionsLoading: true });
        try {
          const res = await api.get<{
            permissions: { code: string; type?: string; children?: any[] }[]
            ad_context?: AdContext | null
          }>('/me/permissions');
          const flatten = (items: { code: string; type?: string; children?: any[] }[]): string[] =>
            items.flatMap(p => [p.code, ...flatten(p.children ?? [])]);
          const codes = flatten(res.permissions ?? []);
          const nextAdContext = res.ad_context ?? null;
          if (codes.length === 0) {
            // 权限为空：标记为无权限，阻止后续 API 调用
            set({ permissionCodes: [], permissionsLoaded: true, permissionsLoading: false, permissionsCheckDone: true, isForbidden: true });
          } else {
            // 权限加载完成后同步校正 siteId，防止持久化的旧 siteId 触发无权限报错
            const st = get();
            const brandName = st.brands.find(b => b.id === st.brandId)?.name?.toLowerCase();
            const hasBrandCodes = hasAnyAdSitePermissionCode(codes);
            const stateUpdate: { brandId?: string; brand?: Brand; siteId?: string; site?: string } = {};
            const allowedBrandIds = nextAdContext?.brand_access ?? [];
            if (allowedBrandIds.length > 0) {
              if (!allowedBrandIds.includes(st.brandId)) {
                stateUpdate.brandId = allowedBrandIds[0];
                stateUpdate.siteId = '';
                stateUpdate.site = '';
                const matchedBrand = st.brands.find((b) => b.id === stateUpdate.brandId);
                if (matchedBrand) {
                  stateUpdate.brand = (matchedBrand.name.toLowerCase() as Brand) || st.brand;
                }
              }

              const effectiveBrandId = stateUpdate.brandId ?? st.brandId;
              const allowedSitesForBrand = nextAdContext?.site_access?.[effectiveBrandId] ?? [];
              const currentSiteId = stateUpdate.siteId ?? st.siteId;
              if (allowedSitesForBrand.length > 0 && currentSiteId && !allowedSitesForBrand.includes(currentSiteId)) {
                stateUpdate.siteId = allowedSitesForBrand[0];
                const matchedSite = st.sites.find((s) => s.id === allowedSitesForBrand[0]);
                if (matchedSite) { stateUpdate.site = matchedSite.code; }
              }
            }
            if (hasBrandCodes && brandName) {
              const slugs = getAllowedSiteSlugsForBrand(codes, brandName);
              if (!slugs.includes('global') && slugs.length === 0) {
                const fallbackBrand = getFirstAccessibleBrand(st.brands, codes);
                if (fallbackBrand && fallbackBrand.id !== st.brandId) {
                  stateUpdate.brandId = fallbackBrand.id;
                  stateUpdate.brand = (fallbackBrand.name.toLowerCase() as Brand) || 'zeelool';
                  stateUpdate.siteId = '';
                  stateUpdate.site = '';
                }
              } else if (st.sites.length > 0 && st.siteId && slugs.length > 0) {
                const allowedSlugs = new Set(slugs.map(s => s.toLowerCase()));
                const allowedIds = new Set(
                  st.sites
                    .filter(s => allowedSlugs.has((s.code?.split('-')[0] || s.code)?.toLowerCase() ?? ''))
                    .map(s => s.id)
                );
                if (!allowedIds.has(st.siteId)) {
                  const first = st.sites.find(s => allowedIds.has(s.id));
                  if (first) { stateUpdate.siteId = first.id; stateUpdate.site = first.code; }
                }
              }
            }
            set({
              permissionCodes: codes,
              permissionsLoaded: true,
              permissionsLoading: false,
              permissionsCheckDone: true,
              ...stateUpdate,
              ...(stateUpdate.brandId ? { sites: [], sitesLoaded: false } : {}),
            });
            if (stateUpdate.brandId) {
              void get().fetchSites(stateUpdate.brandId);
            }
            // 从权限码推导 data_level，确保后台改角色后刷新页面即生效，无需重新登录
            const derivedLevel = codes.includes('ad:role:admin') ? 4
              : codes.includes('ad:role:lead') ? 3
              : codes.includes('ad:role:optimizer') ? 2
              : 1;
            if (nextAdContext?.brand_access) {
              get().setAdContext({ ...nextAdContext, data_level: derivedLevel });
            } else {
              const ctx = get().adContext;
              if (ctx && ctx.data_level !== derivedLevel) {
                get().setAdContext({ ...ctx, data_level: derivedLevel });
              }
            }
          }
        } catch (err) {
          const is403 = (err as any)?.status === 403;
          if (is403) {
            // 权限接口返回 403：标记为无权限
            set({ permissionCodes: [], permissionsLoaded: true, permissionsLoading: false, permissionsCheckDone: true, isForbidden: true });
          } else {
            // gatekeeper 不可用：标记加载完成但放行所有
            set({ permissionCodes: [], permissionsLoaded: false, permissionsLoading: false, permissionsCheckDone: true });
          }
        }
      },
      hasPermission: (code: string) => {
        const { permissionCodes, permissionsLoaded } = get();
        if (!permissionsLoaded) return true;
        return permissionCodes.includes(code);
      },

      // Notifications
      notificationCount: 3,
      setNotificationCount: (count) => set({ notificationCount: count }),
      incrementNotificationCount: () =>
        set((state) => ({ notificationCount: state.notificationCount + 1 })),
      clearNotifications: () => set({ notificationCount: 0 }),
    }),
    {
      name: 'omni-growth-app-storage',
      version: 2,
      partialize: (state) => ({
        brand: state.brand,
        site: state.site,
        brandId: state.brandId,
        siteId: state.siteId,
        sidebarCollapsed: state.sidebarCollapsed,
        channelFilters: state.channelFilters,
        dateRange: state.dateRange,
      }),
      migrate: (persistedState: unknown) => {
        const state = (persistedState as Partial<AppState> | undefined) ?? {};
        const brand = state.brand === 'vooglam' ? 'vooglam' : 'zeelool';
        return {
          ...state,
          brand,
          brandId: normalizeStoredUuid(state.brandId, BRAND_ID_MAP[brand]),
          siteId: normalizeStoredUuid(state.siteId, DEFAULT_SITE_ID),
        };
      },
    }
  )
);

export default useAppStore;
